import React, { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { 
  FiSave, 
  FiPlus, 
  FiTrash2, 
  FiVideo,
  FiType,
  FiBarChart
} from 'react-icons/fi'
import { useContent } from '../../../hooks/useContent'
import { HeroContent } from '../../../types/content'
import VideoPicker from '../VideoPicker'
import toast from 'react-hot-toast'

interface HeroEditorProps {
  onPreview?: (data: HeroContent) => void
}

const HeroEditor: React.FC<HeroEditorProps> = ({ onPreview }) => {
  const { content, loading, saving, saveContent } = useContent('hero')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showVideoPicker, setShowVideoPicker] = useState(false)

  const { register, control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm<HeroContent>({
    defaultValues: {
      title: '',
      subtitle: '',
      description: '',
      videoUrl: '',
      primaryButtonText: '',
      secondaryButtonText: '',
      clickableText: 'Bespoke Curation for Live Events',
      ctaButtonText: 'Get in Touch',
      stats: [
        { number: '', label: '' }
      ]
    }
  })

  const { fields: statsFields, append: appendStat, remove: removeStat } = useFieldArray({
    control,
    name: 'stats'
  })

  const watchedData = watch()

  // Load content when available
  useEffect(() => {
    if (content?.data) {
      reset(content.data as HeroContent)
      setHasUnsavedChanges(false)
    }
  }, [content, reset])

  // Watch for changes to enable unsaved changes indicator
  useEffect(() => {
    if (content?.data) {
      const currentData = JSON.stringify(watchedData)
      const originalData = JSON.stringify(content.data)
      setHasUnsavedChanges(currentData !== originalData)
    }
  }, [watchedData, content])

  // Auto-save functionality (optional)
  useEffect(() => {
    if (hasUnsavedChanges && onPreview) {
      onPreview(watchedData)
    }
  }, [watchedData, hasUnsavedChanges, onPreview])

  const onSubmit = async (data: HeroContent) => {
    const success = await saveContent(data)
    if (success) {
      setHasUnsavedChanges(false)
    }
  }

  const handleAddStat = () => {
    appendStat({ number: '', label: '' })
  }

  const handleRemoveStat = (index: number) => {
    if (statsFields.length > 1) {
      removeStat(index)
    } else {
      toast.error('You must have at least one stat')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-neutral-600">Loading hero content...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Save Button Header */}
      <div className="flex items-center justify-end space-x-3">
        {hasUnsavedChanges && (
          <span className="text-sm text-amber-600 font-medium">
            Unsaved changes
          </span>
        )}
        
        <button
          onClick={handleSubmit(onSubmit)}
          disabled={saving || !hasUnsavedChanges}
          className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg transition-all text-sm font-medium ${
            hasUnsavedChanges && !saving
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-md'
              : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
          }`}
        >
          {saving ? (
            <>
              <div className="loading-spinner w-4 h-4"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <FiSave size={16} />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Main Content */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 mb-6 flex items-center space-x-2">
            <FiType size={18} />
            <span>Main Content</span>
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Title */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Main Title
              </label>
              <input
                {...register('title', { required: 'Title is required' })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="Perfect Music for"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Subtitle (Highlighted Text)
              </label>
              <input
                {...register('subtitle', { required: 'Subtitle is required' })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="Every Event"
              />
              {errors.subtitle && (
                <p className="mt-1 text-sm text-red-600">{errors.subtitle.message}</p>
              )}
            </div>

            {/* Video URL */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Background Video
              </label>
              
              <div className="space-y-3">
                {/* Video Preview */}
                {watch('videoUrl') && (
                  <div className="w-full max-w-md h-32 rounded-xl overflow-hidden border border-neutral-200">
                    {watch('videoUrl').includes('youtube.com') || watch('videoUrl').includes('youtu.be') ? (
                      <iframe
                        src={watch('videoUrl')}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        src={watch('videoUrl')}
                        className="w-full h-full object-cover"
                        controls
                        muted
                        preload="metadata"
                      />
                    )}
                  </div>
                )}

                {/* URL Input with Browse Button */}
                <div className="flex space-x-3">
                  <div className="flex-1 relative">
                    <FiVideo className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
                    <input
                      {...register('videoUrl')}
                      className="w-full pl-10 pr-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder="YouTube embed URL or uploaded video"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowVideoPicker(true)}
                    className="px-4 py-3 bg-neutral-100 text-neutral-700 rounded-xl hover:bg-neutral-200 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <FiVideo size={16} />
                    <span>Browse</span>
                  </button>
                </div>
                
                <p className="text-sm text-neutral-600">
                  Use YouTube embed URL or click "Browse" to select from uploaded videos
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Description
              </label>
              <textarea
                {...register('description', { required: 'Description is required' })}
                rows={3}
                className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-vertical"
                placeholder="From acoustic duos to full bands, we make every event unforgettable."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            {/* Clickable Text */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Clickable Text (with gradient effect)
              </label>
              <input
                {...register('clickableText', { required: 'Clickable text is required' })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="Bespoke Curation for Live Events"
              />
              {errors.clickableText && (
                <p className="mt-1 text-sm text-red-600">{errors.clickableText.message}</p>
              )}
              <p className="mt-1 text-xs text-neutral-500">
                This text appears with a gradient hover effect and links to contact
              </p>
            </div>

            {/* CTA Button Text */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                CTA Button Text
              </label>
              <input
                {...register('ctaButtonText', { required: 'CTA button text is required' })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="Get in Touch"
              />
              {errors.ctaButtonText && (
                <p className="mt-1 text-sm text-red-600">{errors.ctaButtonText.message}</p>
              )}
              <p className="mt-1 text-xs text-neutral-500">
                This appears as a pill-shaped button below the clickable text
              </p>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiBarChart size={18} />
              <span>Statistics</span>
            </h3>
            
            <button
              type="button"
              onClick={handleAddStat}
              className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200"
            >
              <FiPlus size={16} />
              <span>Add Stat</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {statsFields.map((field, index) => (
              <div
                key={field.id}
                className="p-4 border border-neutral-200 rounded-xl space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700">
                    Stat {index + 1}
                  </span>
                  {statsFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveStat(index)}
                      className="text-red-500 hover:text-red-700 transition-colors duration-200"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
                
                <div className="space-y-2">
                  <input
                    {...register(`stats.${index}.number` as const, { required: 'Number is required' })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    placeholder="500+"
                  />
                  <input
                    {...register(`stats.${index}.label` as const, { required: 'Label is required' })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    placeholder="Events Booked"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button (Mobile) */}
        <div className="lg:hidden">
          <button
            type="submit"
            disabled={saving || !hasUnsavedChanges}
            className={`w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-xl transition-all duration-200 ${
              hasUnsavedChanges && !saving
                ? 'bg-primary text-white hover:bg-primary/90'
                : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <div className="loading-spinner w-4 h-4"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FiSave size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Video Picker Modal */}
      <VideoPicker
        isOpen={showVideoPicker}
        onClose={() => setShowVideoPicker(false)}
        onSelect={(videoUrl) => {
          setValue('videoUrl', videoUrl)
          setShowVideoPicker(false)
        }}
        selectedUrl={watch('videoUrl')}
      />
    </div>
  )
}

export default HeroEditor
